(ns web3.routing.core
  (:require [web3.lazy :as lazy]
            [bidi.bidi :as bidi]
            [clojure.string :as str]
            [re-frame.db :refer [app-db]]
            [re-frame.core :as rf]
            [web3.re-frame.interceptors :as intr]
            [web3.utils :as utils]
            [web3.routing.utils :as r-utils]
            [web3.view :as v]
            [re-frame-simple.db :as db]
            [web3.log :as log]
            [web3.ui-state :as ui-state]
            [clojure.string :as str]
            [web3.routing.query :as query]
            [web3.routing.history]
            [web3.routing.utils :as ru]
            [web3.routing.auxiliary :as aux]
            [kitchen-async.promise :as p]
            [web3.re-frame.async :as rf-async]))

;; NOTE
;; the functions in this namespace are mostly not pure,
;; they depend on *index.

(defonce *index (atom {}))

;; ========== Query ==========

(def set-query! query/set-query!)
(def get-query query/get-query)

;; ========== Routers ==========

(defn match-route [router path]
  (or (update (bidi/match-route ((:paths @*index) router) path)
              :route-params
              #(utils/map-vals js/decodeURIComponent %))
      {:handler :handler/not-found}))

;; ========== Path ==========

(defn- make-route-kargs
  "Returns bidi-compliant list of args for `map-args`"
  [handler map-args]
  (->> map-args
       (into [handler] (comp (remove (comp nil? second))
                             (map #(update % 1 js/encodeURIComponent))
                             (mapcat identity)))))

(defn- apply-route-args
  "Returns a path string (or nil) for the given `router` and `args`.

   The :query router args should be a map. It will be merged with the query keys
   currently listened to by components, as tracked in the `queries` atom."
  [router args]
  (cond (or (nil? args)
            (string? args)) args

        (= router :query) (query/query-string
                            (merge
                              ;; keep keys that are being listened to, as tracked in @queries
                              (select-keys (db/get-in (query/query-path)) (keys @query/queries))
                              args))
        :else
        (apply bidi/path-for ((:paths @*index) router) args)))

(defn path-for
  "Return route for given routing data.

   Accepts either:

   - [<map>] A map of form {<router>, <path>}.
     Returns a path containing _only_ the routers/paths in the map.


   - [<router>?, <args>] A `router` key (optional, default is :root) and args vector.
     Returns the current route, with the specified `router`'s path modified based
     on the provided args.

  `router` should be `:root`, or an auxiliary router listed in `web3.routing/routers`.

  Passing `nil` as the <path> or <args> for an auxiliary router will remove that router from the path."
  ([args]
   (try
     (if (map? args)
       (->> args
            (reduce-kv (fn [m router args]
                         (assoc-in m [router :path]
                                   (apply-route-args router args)))
                       {})
            (aux/emit-path))
       (path-for ((:router-lookup @*index) (first args) :router/root) args))
     (catch js/Error e
       (rf/dispatch [:events/handle-error {:source ::path-for
                                           :severity :medium
                                           :error e
                                           :debug-log {:args args}}])
       nil)))
  ([router args & more-routers]
   (let [{:as routers
          :keys [query]} (->> (cons router (cons args more-routers))
                              (apply hash-map))]
     (->> (merge (utils/map-vals :path (db/get :db/routers))
                 routers
                 {:query query})
          (path-for)))))

(defn update-path
  "Apply `f` to current path of `router` with `args."
  [router f & args]
  (path-for router
            (apply f (db/get-in [:db/routers router :path]) args)))

;; ========== Breadcrumbs ==========

(defn breadcrumbs
  "Returns a vector of matched routes, starting from root.

  Each route is a map containing `:handler`, `:path`, and (opt) `:route-params`."
  [router]
  (when-let [path (db/get-in [:db/routers router :path])]
    (let [segments (str/split path #"/")
          paths (distinct (for [n (range 0 (inc (count segments)))]
                            (->> (take n segments)
                                 (str/join "/"))))]
      (vec (for [path paths
                 :let [{:keys [handler] :as match} (bidi/match-route ((:paths @*index) router) path)]
                 :when match]
             (assoc match
               :path path
               :view (-> (:handlers @*index)
                         (get handler)
                         (lazy/loadable-lookup :view)
                         (lazy/wrap-view))))))))

(defn set-prev-breadcrumbs!
  ([] (set-prev-breadcrumbs! :router/root))
  ([router]
   (fn [{:keys [state]}]
     (v/assoc-prev! state :breadcrumbs (breadcrumbs router)))))

(defn drop-segments
  "Returns longest valid path for `router` after dropping its last path segment."
  [n router]
  (path-for router
            (->> (breadcrumbs router)
                 (drop-last n)
                 (last)
                 :path)))

;; ========== Href ==========

(defn conj-segment [router segment]
  (update-path router (fn [s]
                        (cond-> s
                                (not (str/ends-with? (or s "") segment))
                                (str "/" segment)))))

;; ========== Register ==========

(defn- normalize-paths [router-key paths]
  {:pre [(and (vector? paths) (string? (first paths)))]}
  ;; paths for :router/root must start with "/",
  ;; other paths must _not_ start with "/"
  (let [f (if (= router-key :router/root)
            utils/ensure-prefix
            utils/trim-leading-char)]
    (update paths 0 (partial f "/"))))

(defn- normalize-handler [handler]
  ;; handler is stored as {:view <fn> ...opts}
  ;; but can be passed in as simply <fn>
  (if (map? handler) handler {:view handler}))

(defn merge-paths [p1 p2]
  (if p1
    (update p1 1 merge (nth p2 1))
    p2))

(def into-map (fnil into {}))

(defn index-router [index [router-key {:keys [paths handlers]}]]
  (let [;; the set of handlers listed in `paths` is canonical -
        ;; handlers not corresponding to a path are ignored.
        handler-keys (map :handler (bidi/route-seq paths))
        ;; handlers are looked up from a possibly-lazy handlers map
        get-handler (fn [k] (lazy/loadable-lookup handlers (comp normalize-handler k)))]
    (-> index
        (update :handlers into-map
                (map (juxt identity get-handler) handler-keys))
        (update-in [:paths router-key] merge-paths
                   (normalize-paths router-key paths))
        (update :router-lookup into-map
                (map (juxt identity (constantly router-key)) handler-keys)))))

(defn register! [routers not-found]
  (swap! *index
         (fn [index]
           (let [index (assoc-in index [:handlers :handler/not-found] (normalize-handler not-found))]
             (->> routers
                  (mapcat (fn [[router-key routers]]
                            (for [router (if (map? routers) [routers] routers)]
                              [router-key router])))
                  (reduce index-router index))))))

;; ========== Resolve ==========

(defn resolve-routes
  "Given a map of {<router-key>, <path-or-data>}, matches paths to handlers."
  [router-paths]
  (p/->> router-paths
         (mapv (fn [[router-key path-or-data]]
                 (if (= :query router-key)
                   [router-key path-or-data]
                   (let [handler-data (match-route router-key path-or-data)
                         handler-data (update handler-data :route-params assoc :handler (:handler handler-data))]
                     (if-let [view-entry (get (:handlers @*index) (:handler handler-data))]
                       (p/let [view-entry (lazy/load-lazy view-entry)
                               view (lazy/load-lazy (:view view-entry))]
                         [router-key
                          (merge handler-data
                                 view-entry
                                 {:view view
                                  :router router-key
                                  :path path-or-data})])
                       (do (log/println :warn "missing handler for route" (:handler handler-data))
                           nil))))))
         (p/all)
         (into {})))

;; ========== Events ==========

(rf/reg-fx
  :window/scroll-up
  (fn [router]
    ;; NOTE
    ;; if we need to scroll up other routers, then we'll need
    ;; to find the element that handles the scroll behaviour
    (when-let [element (case router
                         :router/root js/window
                         :router/drawer (when (ui-state/mobile?)
                                          (.getElementById js/document "drawer")))]
      (.scroll element #js {:top 0
                            :left 0
                            :behaviour "smooth"}))))



(defn- history-transition-fx [prev-routers next-routers]
  (let [mobile? (ui-state/mobile?)
        prev-root (:router/root prev-routers)
        next-root (:router/root next-routers)
        set? (if mobile?
               ;; on mobile devices, we set more history states because modals are presented
               ;; as full pages.
               (not= (dissoc next-routers :query)
                     (dissoc prev-routers :query))
               (or
                 ;; root top-level category has changed
                 (not= (utils/simplify-keyword (:handler next-root))
                       (utils/simplify-keyword (:handler prev-root)))
                 ;; property has changed
                 (not= (get-in next-root [:route-params :property-id])
                       (get-in prev-root [:route-params :property-id]))))]
    (if set?
      :history/set
      :history/replace)))

(intr/reg-event-fx
  :routing/nav
  (fn [cofx [_ next-href]]
    {::rf-async/then
     (p/let [{:as next-routers
              next-drawer :router/drawer
              next-root :router/root} (->> next-href
                                           (aux/parse-path)
                                           (resolve-routes))]
       (let [{:as prev-routers
              prev-drawer :router/drawer
              prev-root :router/root} (db/get :db/routers)
             history-fx (history-transition-fx prev-routers next-routers)
             scroll-up-router (cond (and (not= (:path next-root)
                                               (:path prev-root))
                                         ;; do not touch scrolling when transitioning away from home screen
                                         (not= :home (:handler prev-root))) :router/root
                                    (not= next-drawer prev-drawer) :router/drawer)]
         (log/println :debug (str " 👉 " (if set? "set" "replace") ": " next-href))

         (merge {:intercom/update-route nil}
                {history-fx next-href}
                (when scroll-up-router {:window/scroll-up scroll-up-router}))))}))

(defn nav!
  [href]
  (rf/dispatch [:routing/nav href]))

;; ========== Listen ==========

(def listen!
  "Begin listening to changes in pushState routes"
  (delay
    (ru/listen
      (fn [location]
        (p/let [resolved-routers (->> (:path location)
                                      (aux/parse-path)
                                      (resolve-routes))]
          (rf/dispatch [:routing/handle resolved-routers]))))

    ;; intercept clicks, decide whether to setToken or replaceToken
    (r-utils/intercept-clicks nav!)))

(defn init! [{:keys [routers
                     interceptors
                     handler/not-found]}]
  (register! routers not-found)

  (intr/reg-event-fx
    :routing/handle
    interceptors
    (fn [{:keys [db]} [_ resolved-routers]]
      {:db (-> db
               (dissoc :errors :db/routers)
               (assoc :db/routers resolved-routers))}))

  @listen!)



(comment
  (path-for :query {:x 1 :z nil})
  (assert (= (println (ru/query (path-for :query {:x 1 :z nil})))
             {:x 1})
          "Round-trip query params"))



(comment
  (bidi/match-route ["" {#".*(select-parent)" :handler/select-parent}]
                    "a/b/c/select-parent"))

(comment
  (= "/"
     (path-for [:home])                                     ;; if drawer is currently closed and no query
     (path-for {:router/root [:home]})
     (path-for {})
     (path-for {:router/root [:home]
                :router/drawer nil}))

  (path-for {:router/drawer nil})
  (= "/(drawer)"
     (path-for {:router/root [:home]
                :router/drawer [:drawer/home]}))
  (= "/(drawer:sponsor)"
     (path-for {:router/root [:home]
                :router/drawer [:drawer/sponsor]})
     (path-for {:router/drawer [:drawer/sponsor]})))
